/**
 * Beispielfüllungen für die Vorlagen-Galerie: So sieht die fertige Mail aus.
 * Die Termine liegen immer ein paar Tage in der Zukunft, damit das Beispiel
 * nicht veraltet.
 */
import type { Antworten, Daten } from './vorlagen';

export interface Beispiel {
  schulen: string[];
  antworten: Antworten;
  daten: Daten;
}

function inTagen(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Nächster Montag, frühestens in `n` Tagen. */
function montagAb(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const JA = { abgestimmt: 'ja', fakten: 'ja' };

export const BEISPIELE: Record<string, () => Beispiel> = {
  'kurzfristige-aenderung': () => ({
    schulen: ['Mariendorf'],
    antworten: { ersatz: 'ja', probe: 'nein', ...JA },
    daten: {
      art: 'faellt-aus', kurs: 'Kids Kickboxen 6–9 Jahre', datum: inTagen(3), uhrzeit: '17:00–18:00',
      grund: 'Unser Trainer ist leider krank', ersatztext: 'Nachholtermin am **Freitag um 17:00 Uhr** — oder kommt gern in den Kurs am Donnerstag.',
      zusatz: '', anrede: 'mitglieder-eltern', schluss: 'Danke für Euer Verständnis — wir sehen uns auf der Matte!', bild: '',
    },
  }),
  stundenplan: () => ({
    schulen: ['Steglitz'],
    antworten: { crm: 'ja', pdf: 'ja', ...JA },
    daten: {
      ab: montagAb(10), titel: 'Euer Stundenplan: Änderungen ab Montag',
      einleitung: 'ab dem neuen Monat gibt es ein paar Änderungen im Stundenplan — vor allem bei den Kids-Kursen am Nachmittag. Hier seht Ihr alles auf einen Blick.',
      aenderungen: 'Mo 17:00 Kids Karate 6–9 → neu um **17:30 Uhr**\nNeu: Do 18:00 MMA Teens (13–17 Jahre)\nFr 19:00 Kickboxen Erwachsene entfällt',
      pdf: 'https://randori-pro.de/stundenplan', checkin: 'ja', anrede: 'mitglieder-eltern', bild: '',
      schluss: 'Wir freuen uns auf Euch — bis bald auf der Matte!',
    },
  }),
  schliesszeit: () => ({
    schulen: ['Tegel'],
    antworten: { crm: 'ja', ...JA },
    daten: {
      anlass: 'Herbstferien', von: inTagen(14), bis: inTagen(20), wieder: inTagen(21),
      ausnahmen: 'Das Wettkampftraining am Samstag findet trotzdem statt.', anrede: 'mitglieder-eltern',
      schluss: 'Wir wünschen Euch schöne Ferien — bis bald auf der Matte!', bild: '',
    },
  }),
  erinnerung: () => ({
    schulen: ['Spandau'],
    antworten: { frist: 'nein', link: 'nein', ...JA },
    daten: {
      thema: 'Check-in am Terminal', titel: 'Bitte denkt an den Check-in',
      text: 'kurze Erinnerung: Bitte haltet vor jedem Training **Euer Armband oder Eure Karte ans Terminal** und wählt den Kurs aus, an dem Ihr teilnehmt.',
      frist: '', wichtig: 'Ohne Check-in sieht unser Trainer nicht, wer da ist — und die Kurse lassen sich nicht richtig planen.',
      knopf: '', url: '', anrede: 'mitglieder-eltern', schluss: 'Danke Euch — bis bald auf der Matte!', bild: '',
    },
  }),
  einladung: () => ({
    schulen: ['Spandau'],
    antworten: { eventtool: 'klein', anmeldung: 'online', 'link-live': 'ja', ...JA },
    daten: {
      anlass: 'Notwehr-Seminar', titel: 'Selbstbewusst im Ernstfall', untertitel: 'Für alle ab 14 Jahren – nur 30 Plätze',
      datum: inTagen(18), uhrzeit: '10:00–14:00', ort: 'Randori-Pro Spandau, Askanierring 155, 13585 Berlin',
      fuerwen: 'Mitglieder ab 14 Jahren', kosten: 'kostenlos für Mitglieder',
      einleitung: 'wie verhalte ich mich, wenn es ernst wird? In unserem Notwehr-Seminar lernt Ihr, Situationen früh zu erkennen und Euch wirksam zu befreien.',
      punkte: 'Praxis statt Theorie: Befreiungen aus Griffen, die wirklich funktionieren\nRecht verständlich: Was Notwehr darf — und was nicht\nFür alle: Kein Vorwissen nötig',
      knopf: 'Jetzt Platz sichern', url: 'https://randori-pro.de', schluss_anmeldung: inTagen(15), hinweis: 'Bitte bequeme Sportkleidung und etwas zu trinken mitbringen.',
      anrede: 'mitglieder', bild: '', schluss: 'Wir freuen uns auf Euch!',
    },
  }),
  guertelpruefung: () => ({
    schulen: ['Wilmersdorf'],
    antworten: { fest: 'ja', zuschauen: 'abschluss', ...JA },
    daten: {
      datum: inTagen(24), einlass: '09:30', ort: 'Randori-Pro Wilmersdorf, Berliner Straße 30, 10715 Berlin',
      gruppen: 'Weiß-Gelb Kids 6–9: 10:00–11:00 Uhr\nGelb-Orange Kids 10–13: 11:15–12:30 Uhr',
      voraussetzungen: 'Vorprüfung bestanden\nPrüfungsgebühr bezahlt',
      mitbringen: 'Sauberer Gi mit aktuellem Gürtel\nTrinkflasche\nPrüfungsheft', gebuehr: '25 €, bar am Prüfungstag',
      anrede: 'eltern', schluss: 'Wir drücken allen die Daumen — Ihr schafft das!', bild: '',
    },
  }),
  'eltern-info': () => ({
    schulen: ['Lübars'],
    antworten: { gruppe: 'gruppe', ...JA },
    daten: {
      thema: 'Ferienprogramm Kids', titel: 'Ferienspaß in den Herbstferien',
      text: 'in den Herbstferien bieten wir für alle Kids von **6 bis 13 Jahren** ein Ferienprogramm an: Training, Spiele und ein gemeinsames Mittagessen.',
      schritte: 'Bis Freitag am Tresen anmelden\nMontag um 9:00 Uhr bringen\nUm 15:00 Uhr abholen',
      packliste: 'Sportkleidung und Hallenschuhe\nTrinkflasche\nKleiner Snack', wichtig: '',
      anrede: 'eltern', bild: '', schluss: 'Danke Euch — bis bald auf der Matte!',
    },
  }),
  'freie-nachricht': () => ({
    schulen: ['Waltersdorf'],
    antworten: { link: 'nein', ...JA },
    daten: {
      symbol: '🏆', thema: 'Neuigkeiten', titel: 'Unser Kader räumt ab', untertitel: 'Sieben Medaillen beim Herbstturnier',
      text: 'was für ein Wochenende! Unser Kader hat beim Herbstturnier **sieben Medaillen** geholt — darunter drei Mal Gold.\n\nDanke an alle, die angefeuert haben.',
      kasten_titel: '📅 Nächstes Turnier', kasten_text: 'Im Dezember geht es weiter. Wer mitmachen möchte, meldet sich beim Trainer.',
      knopf: '', url: '', anrede: 'mitglieder-eltern', bild: '', schluss: 'Wir freuen uns auf Euch — bis bald!',
    },
  }),
};
